import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './NookPhone.css';
import { getMiles, getKitToken } from '../utils/storage';

interface NookPhoneProps {
  children: React.ReactNode;
}

export function NookPhone({ children }: NookPhoneProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [time, setTime] = useState(new Date());
  const [miles, setMiles] = useState(getMiles());
  
  // Track if user has accepted instructions (token exists)
  const [hasToken, setHasToken] = useState(!!getKitToken());

  // Keep clock updated
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Sync token state on location change
  useEffect(() => {
    setHasToken(!!getKitToken());
    setMiles(getMiles());
  }, [location.pathname]);

  // Route guard: force instructions if not tokenized yet
  useEffect(() => {
    if (!getKitToken() && location.pathname !== '/instructions') {
      navigate('/instructions');
    }
  }, [location.pathname, navigate]);

  const hours = time.getHours();
  const minutes = time.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');

  const isHome = location.pathname === '/' || location.pathname === '/phone';
  
  // Only show back-header if we are not on home, AND (not on instructions OR we have token)
  const showHeader = !isHome && (location.pathname !== '/instructions' || hasToken);

  return (
    <div className="nook-phone-outer">
      <div className="nook-phone-body">
        {/* Top Speaker & Sensor */}
        <div className="nook-phone-speaker-bar">
          <span className="nook-phone-sensor"></span>
          <span className="nook-phone-speaker"></span>
        </div>

        {/* The screen container */}
        <div className="nook-phone-screen">
          {/* Status Bar */}
          <div className="nook-phone-status-bar">
            <div className="nook-phone-status-left">
              <svg className="nook-status-icon" viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M12 21a2 2 0 1 1-2-2 2 2 0 0 1 2 2zm7.07-7.07a10 10 0 0 0-14.14 0 1 1 0 1 0 1.41 1.41 8 8 0 0 1 11.31 0 1 1 0 1 0 1.42-1.41zm3.54-3.54a15 15 0 0 0-21.22 0 1 1 0 1 0 1.42 1.42 13 13 0 0 1 18.38 0 1 1 0 1 0 1.42-1.42z" />
              </svg>
              <span className="nook-operator">NookLink</span>
            </div>
            
            <div className="nook-phone-status-center">
              {displayHours}
              <span className="nook-time-colon">:</span>
              {displayMinutes}
              <span className="nook-time-ampm">{ampm}</span>
            </div>

            <div className="nook-phone-status-right">
              <div className="nook-battery-icon">
                <div className="nook-battery-body">
                  <div className="nook-battery-fill"></div>
                </div>
                <div className="nook-battery-tip"></div>
              </div>
            </div>
          </div>

          {/* Navigation Bar inside App */}
          {showHeader && (
            <div className="nook-phone-app-header">
              <button className="nook-back-button" onClick={() => navigate(-1)}>
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                Back
              </button>
              <div className="nook-miles-badge">
                <span className="miles-leaf">🍃</span>
                <span className="miles-value">{miles}</span>
                <span className="miles-label">Points</span>
              </div>
            </div>
          )}

          {/* Screen Viewport */}
          <div className="nook-phone-screen-content">
            {children}
          </div>
        </div>

        {/* Home Button at bottom of frame */}
        <div className="nook-phone-home-button-area">
          <button 
            className={`nook-phone-home-button ${isHome ? 'active' : ''}`}
            onClick={() => {
              if (hasToken) navigate('/');
            }}
            disabled={!hasToken}
            title="Go to Home Screen"
          >
            <div className="home-button-circle-inner"></div>
          </button>
        </div>
      </div>
    </div>
  );
}
